<!--
  FieldPlanTaskDuration.vue
  Duration cell for a structured/half-structured plan's task list (StructuredPlan/Search rows).

  A placement may pin its OWN duration for this program instead of inheriting the shared bank's,
  so the row carries four fields and each has a distinct job:
    durationMinutes         — the RESOLVED value the plan actually uses. This is what the cell
                              displays at rest, so the column always answers "how long is this
                              task" with one unambiguous number.
    durationMinutesOverride — what the input binds to once editing starts; null = inheriting
    bankDurationMinutes     — what inheriting gives. Shown as a labelled line in the editor and
                              in the tooltip — deliberately NOT as the input's placeholder: the
                              bank value is live data, and rendering it in placeholder gray reads
                              as "not a real value" while it is in fact the one in effect.
    isDurationOverridden    — drives the badge. Without it an override is invisible state: an
                              admin later edits the bank and reports "the plan didn't update" as a bug.

  Display at rest, input on click — so the grid stays scannable and the reference value doesn't
  vanish at the moment the admin is typing against it.

  Clearing the input saves null, which CLEARS the override (the PUT has no partial semantics —
  null means "clear", not "leave alone"). The row's schedule fields are echoed back untouched
  for the same reason.
-->
<template>
  <div class="Field__Component FieldPlanTaskDuration" @click.stop>
    <!-- ===== at rest — the effective duration, one number ===== -->
    <b-tooltip v-if="!isEditing" :label="tooltip" position="is-left" multilined>
      <button type="button" class="dur-display" :class="{ 'is-overridden': isOverridden, 'is-saving': isSaving }"
        :disabled="isSaving" @click="startEdit">
        <span class="num">{{ resolvedMinutes == null ? '—' : resolvedMinutes }}</span>
        <span class="unit">דק'</span>
        <span v-if="isOverridden" class="badge">מותאם</span>
      </button>
    </b-tooltip>

    <!-- ===== editing — the override, with the bank value kept in view beside it ===== -->
    <div v-else class="dur-edit">
      <div class="dur-edit-row">
        <input
          ref="input"
          type="number"
          min="1"
          step="1"
          class="dur-input"
          :class="{ 'is-invalid': !!invalid }"
          :disabled="isSaving"
          :value="draft"
          @input="draft = $event.target.value"
          @blur="onBlur"
          @keyup.enter="$event.target.blur()"
          @keydown.esc="cancel" />
        <span class="dur-unit">דק'</span>
      </div>
      <!-- The bank's duration as a labelled value, not a placeholder — it stays visible while
           typing, which is exactly when the admin wants to compare against it. -->
      <p class="dur-bank">
        <template v-if="bankMinutes == null">ריק = משך מבנק המשימות</template>
        <template v-else>ריק = {{ bankMinutes }} דק' מבנק המשימות</template>
      </p>
      <p v-if="invalid" class="dur-error">{{ invalid }}</p>
    </div>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from "buefy";
import TasksService from "../../services/TasksService";

// Mirrors the server's own rejection message, so the client-side block reads identically.
const INVALID_MSG = "משך המשימה חייב להיות גדול מאפס. יש להשאיר את השדה ריק כדי להשתמש במשך מבנק המשימות.";

export default {
  name: "FieldPlanTaskDuration",
  // `value` is the row's durationMinutes (resolved); the override/bank fields come off `item`.
  props: ["value", "item", "field", "search"],
  data() {
    return {
      isEditing: false,
      draft: "",
      isSaving: false,
      invalid: "",
    };
  },
  computed: {
    isOverridden() {
      return !!(this.item && this.item.isDurationOverridden);
    },
    // What the plan actually uses. Prefer the row so the cell stays correct even if the
    // column is ever pointed at a different field Id.
    resolvedMinutes() {
      const r = this.item && this.item.durationMinutes;
      if (r != null) return r;
      return this.value == null ? null : this.value;
    },
    bankMinutes() {
      const b = this.item && this.item.bankDurationMinutes;
      return b == null ? null : b;
    },
    tooltip() {
      if (this.isOverridden) {
        const bank = this.bankMinutes == null ? "—" : this.bankMinutes;
        return `זמן מותאם לתוכנית זו (בבנק: ${bank} דק'). שינוי המשך בבנק לא ישפיע על משימה זו. לחץ לעריכה — נקה את השדה כדי לחזור למשך מבנק המשימות.`;
      }
      return "משך מבנק המשימות — עדכון בבנק יעדכן גם כאן. לחץ כדי לקבוע משך ייחודי לתוכנית זו.";
    },
  },
  watch: {
    // The list re-renders rows in place after a search/refresh; drop back to display so the
    // cell shows the server's resolved value rather than a stale draft.
    item() {
      this.isEditing = false;
      this.invalid = "";
    },
  },
  methods: {
    overrideOf(row) {
      const v = row && row.durationMinutesOverride;
      return v == null ? "" : String(v);
    },
    startEdit() {
      // Seed from the OVERRIDE, not the resolved value: opening the editor on an inheriting
      // row must not pre-fill the bank's number, or a stray blur would pin it.
      this.draft = this.overrideOf(this.item);
      this.invalid = "";
      this.isEditing = true;
      this.$nextTick(() => {
        const el = this.$refs.input;
        if (el) { el.focus(); el.select(); }
      });
    },
    cancel() {
      this.isEditing = false;
      this.invalid = "";
    },
    // Esc sets isEditing false before the input's blur fires; bailing here keeps the
    // discarded draft from being saved on the way out.
    onBlur() {
      if (!this.isEditing) return;
      this.commit();
    },
    commit() {
      const raw = String(this.draft == null ? "" : this.draft).trim();
      const current = this.overrideOf(this.item);
      if (raw === current) { this.cancel(); return; }

      // Empty clears the override; anything else must parse to a whole number > 0.
      // The server enforces this too, but blocking here avoids a round trip that would
      // reject the whole batch.
      let next = null;
      if (raw !== "") {
        const n = Number(raw);
        if (!Number.isInteger(n) || n <= 0) {
          this.invalid = INVALID_MSG;
          Toast.open({ type: "is-warning", message: INVALID_MSG, duration: 6000 });
          // Stay in edit mode so the bad value is still there to correct.
          this.$nextTick(() => { const el = this.$refs.input; if (el) el.focus(); });
          return;
        }
        next = n;
      }
      this.invalid = "";
      this.save(next);
    },
    // One-row batch. Every field the endpoint receives is written as sent, so the row's
    // schedule and order are echoed back verbatim — only DurationMinutes changes here.
    save(next) {
      const row = this.item;
      this.isSaving = true;
      TasksService.updateStructuredPlanTasks({
        Id: row.id,
        TaskDate: row.taskDate,
        SortOrder: row.sortOrder,
        StartTime: row.startTime || null,
        EndTime: row.endTime || null,
        TimeWindow: row.timeWindow,
        DurationMinutes: next,
      })
        .then(() => {
          Toast.open({
            type: "is-success",
            message: next == null ? "המשך חזר למשך מבנק המשימות" : "משך המשימה עודכן",
            duration: 4000,
          });
          this.isEditing = false;
          // Refresh the list so durationMinutes/isDurationOverridden come back resolved
          // by the server rather than being guessed here.
          if (this.search) this.search();
        })
        .catch((error) => {
          const data = (error && error.response && error.response.data) || {};
          Toast.open({
            type: "is-danger",
            message: data.Error || data.error || "עדכון משך המשימה נכשל",
            duration: 8000,
          });
          this.cancel();
        })
        .finally(() => { this.isSaving = false; });
    },
  },
};
</script>

<style scoped lang="scss">
.FieldPlanTaskDuration { direction: rtl; }

/* ===== at rest ===== */
.dur-display {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid #dbe4ee;
  border-radius: 20px;
  background: #eef3f8;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.12s, background 0.12s;
  .num { font-size: 15px; font-weight: 800; color: #17437A; }
  .unit { font-size: 11px; font-weight: 600; color: #6b7b8c; }
  &:hover:not(:disabled) { border-color: #287DE6; background: #e6eef7; }
  &:disabled { opacity: 0.55; cursor: default; }
}
/* The override marker — an override that isn't visible reads as a bug later. */
.dur-display .badge {
  align-self: center;
  font-size: 9.5px;
  font-weight: 700;
  color: #0a7d74;
  border-inline-start: 1px solid #b9e3de;
  padding-inline-start: 6px;
  margin-inline-start: 2px;
  white-space: nowrap;
}
.dur-display.is-overridden {
  background: #eef6f5;
  border-color: #b9e3de;
  .num { color: #0a7d74; }
  &:hover:not(:disabled) { background: #e3f2f0; border-color: #0a7d74; }
}

/* ===== editing ===== */
.dur-edit-row { display: inline-flex; align-items: center; gap: 5px; }
.dur-input {
  width: 58px;
  height: 30px;
  text-align: center;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  color: #17437A;
  border: 1.5px solid #287DE6;
  border-radius: 4px;
  background: #fff;
  outline: none;
  &:disabled { opacity: 0.55; cursor: default; }
  &.is-invalid { border-color: #E1583D; background: #fdf1ef; }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
  appearance: textfield;
}
.dur-unit { font-size: 11px; color: #6b7b8c; }
.dur-bank { margin-top: 3px; font-size: 10.5px; color: #6b7b8c; white-space: nowrap; }
.dur-error { margin-top: 3px; font-size: 10.5px; color: #E1583D; max-width: 190px; line-height: 1.35; }
</style>
